// dependencies
import * as Sentry from "@sentry/react";
import { fromJS } from "immutable";
import { CreateProcess } from "redux-process";

import get from "../../utils/GetNested";
// utils
import serializeObject from "../../utils/SerializeObject";

export const GetHrisIntegrationInfo = CreateProcess({
	method: "GET",
	name: "GetHrisIntegrationInfo",
	type: "INTEGRATIONS:SETUP",

	request: () => ({
		url: "/api/integrations/enabled/",
	}),

	success: (response) =>
		fromJS({
			name: response.data.name, // string
			connected: response.data.connected, // bool
			walkthroughStatus: response.data.walkthroughStatus, // string
			needsConsent: response.data.needsConsent, // bool
			lastUpdate: response.data.lastUpdate, // string
			connectUrl: response.data.connectUrl, // string
			adpToken: response.data.token, // string
			externalCompanyID: response.data.externalCompanyID, // string
			organization: response.data.organization, // string
			isCredentialResetting: response.data?.isCredentialResetting ? response.data?.isCredentialResetting : false, // bool
		}),
});

export const GetHrisIntegrationUsers = CreateProcess({
	method: "GET",
	name: "GetHrisIntegrationUsers",
	type: "INTEGRATIONS:GETUSERS",

	request: (props) => {
		const { query } = props;
		const queryString = query ? serializeObject(query) : "";

		return {
			url: `/api/integrations/${props.integration}/users/?${queryString}`,
		};
	},

	success: (response) => {
		const { data } = response;

		try {
			const { pageCount } = data;

			const users = data.users.map((user) => ({
				adpUserID: user.adpID,
				adpEmails: user.emails,
				connectionStatus: user.status,
				adpFieldProps: user.adpData,
				deactivated: user.deactivated,
				adpCompanyID: user.adpCompanyID,
				matchedUserObject: user.matchedUserObject,
				performyardCompanyID: user.performyardCompanyID,
			}));

			return fromJS({ users, pageCount });
		} catch (ermahgerd) {
			Sentry.captureException(ermahgerd);
			return [];
		}
	},
});

export const getHrisIntegrationFields = CreateProcess({
	method: "GET",
	name: "GetHrisIntegrationFields",
	type: "INTEGRATIONS:GETFIELDS",

	request: (props) => ({
		url: `/api/integrations/${props.integration}/fields/`,
	}),

	success: (response) => {
		const { data } = response;
		try {
			return {
				fields: fromJS(data.fields),
				externalFields: data.external_fields,
			};
		} catch (ermahgerd) {
			Sentry.captureException(ermahgerd);
			return [];
		}
	},
});

export const setIntegrationCredentials = CreateProcess({
	method: "POST",
	name: "SetIntegrationCredentials",
	type: "INTEGRATIONS:CREDENTIALS.SEND",

	request: (props) => {
		const { integrationType, fields } = props;
		return {
			url: `/api/integrations/${integrationType}/credentials/`,
			payload: fields,
		};
	},

	success: (response) => {
		const { data } = response;
		return fromJS(data);
	},
});

export const getIntegrationCredentials = CreateProcess({
	method: "GET",
	name: "GetIntegrationCredentials",
	type: "INTEGRATIONS:CREDENTIALS.GET",

	request: (props) => {
		const { integrationType } = props.params;
		return {
			url: `/api/integrations/${integrationType}/credentials/`,
		};
	},

	success: (response) => {
		const { data } = response;
		return data;
	},
});

export const sendIntegrationConnections = CreateProcess({
	method: "POST",
	name: "SendIntegrationConnections",
	type: "INTEGRATIONS:CONNECTIONS.SEND",

	request: (props) => ({
		url: `/api/integrations/${props.integration}/connections/`,
		payload: props.payload,
	}),

	success: (response) => {
		const { data } = response;

		try {
			return fromJS(data.fields);
		} catch (ermahgerd) {
			Sentry.captureException(ermahgerd);
			return [];
		}
	},
});

export const sendIntegrationFields = CreateProcess({
	method: "POST",
	name: "SendIntegrationFields",
	type: "INTEGRATIONS:FIELDS.SEND",

	request: (props) => ({
		url: `/api/integrations/${props.integration}/fields/`,
		payload: props.payload,
	}),

	success: (response) => {
		const { data } = response;

		try {
			return fromJS(data.fields);
		} catch (ermahgerd) {
			Sentry.captureException(ermahgerd);
			return [];
		}
	},
});

export const resyncIntegrationWorkers = CreateProcess({
	method: "POST",
	name: "ResyncIntegrationWorkers",
	type: "INTEGRATIONS:RESYNC.SEND",

	request: (props) => ({
		url: `/api/integrations/${props.integration}/fetch-users/`,
		// payload: props.payload,
	}),

	success: () => {
		try {
			return fromJS({});
		} catch (ermahgerd) {
			Sentry.captureException(ermahgerd);
			return [];
		}
	},
});

// re- fetches the integration's external fields
export const resyncIntegrationExternalFields = CreateProcess({
	method: "GET",
	name: "ResyncIntegrationExternalFields",
	type: "INTEGRATIONS:RESYNCEXTERNALFIELDS.SEND",

	request: () => ({
		url: "/api/integrations/fetch-integration-external-fields/",
	}),

	success: (response) => {
		try {
			if (response.succeeded === true) {
				return { success: true, request_id: response.request_id, status_code: response.status_code };
			}

			Sentry.captureException(
				`Error attempting to invoke lambda function fetch-integration-external-fields: ${response}`
			);
			return { success: false };
		} catch (ermahgerd) {
			Sentry.captureException(ermahgerd);
			return [];
		}
	},
});

export const sendBambooHRSettings = CreateProcess({
	method: "POST",
	name: "SendBambooHRSettings",
	type: "INTEGRATIONS:BAMBOOHRKEY.SEND",

	request: (props) => ({
		url: `/api/integrations/${props.integration}/settings/`,
		payload: props.payload,
	}),

	success: (response) => {
		try {
			return fromJS(response.data);
		} catch (ermahgerd) {
			Sentry.captureException(ermahgerd);
			return [];
		}
	},
});

export const sendNetchexSettings = CreateProcess({
	method: "POST",
	name: "SendNetchexSettings",
	type: "INTEGRATIONS:NETCHEXSETTINGS.SEND",

	request: (props) => ({
		url: "/api/integrations/netchex/settings/",
		payload: props.payload,
	}),

	success: (response) => {
		try {
			return fromJS(response.data);
		} catch (ermahgerd) {
			Sentry.captureException(ermahgerd);
			return [];
		}
	},
});

export const addSftpIntegration = CreateProcess({
	method: "POST",
	name: "addSftpIntegration",
	type: "INTEGRATIONS:ADD_SFTP",

	request: (props) => {
		const data = get(props, ["params"], props);
		return {
			url: "/api/integrations/sftp/",
			payload: data,
		};
	},

	success: (response) => {
		try {
			return response.data;
		} catch (ermahgerd) {
			Sentry.captureException(ermahgerd);
			return [];
		}
	},
});

export const getSftpIntegrations = CreateProcess({
	method: "GET",
	name: "getSftpIntegrations",
	type: "INTEGRATIONS:GET_SFTP",

	request: () => ({
		url: "/api/integrations/sftp/",
	}),

	success: (response) => {
		try {
			return response.data;
		} catch (ermahgerd) {
			Sentry.captureException(ermahgerd);
			return [];
		}
	},
});

export const getSftpIntegration = CreateProcess({
	method: "GET",
	name: "getSftpIntegration",
	type: "INTEGRATIONS:GET_SFTP_INTEGRATION",

	request: (props) => {
		if (props.params.integrationID) {
			return {
				url: `/api/integrations/sftp/${props.params.integrationID}`,
			};
		}
		return { url: "/api/integrations/sftp/" };
	},

	success: (response) => {
		try {
			return response.data;
		} catch (ermahgerd) {
			Sentry.captureException(ermahgerd);
			return [];
		}
	},
});

export const UpdateGustoExternalCompanyID = CreateProcess({
	method: "POST",
	name: "UpdateGustoExternalCompanyID",
	type: "INTEGRATIONS:UPDATE_GUSTO_EXTERNAL_COMPANY_ID",

	request: (props) => ({
		url: "/api/integrations/gusto/settings/",
		payload: props.params,
	}),

	success: (response) => {
		try {
			return response.data;
		} catch (ermahgerd) {
			Sentry.captureException(ermahgerd);
			return [];
		}
	},
});

export const editSftpIntegration = CreateProcess({
	method: "PATCH",
	name: "editSftpIntegration",
	type: "INTEGRATIONS:EDIT_SFTP",

	request: (props) => {
		const integrationID = get(props, ["params", "integrationID"], props.integrationID);
		const payload = get(props, ["params", "payload"], props.payload);
		return {
			url: `/api/integrations/sftp/${integrationID}`,
			payload,
		};
	},

	success: (response) => {
		try {
			return response.data;
		} catch (ermahgerd) {
			Sentry.captureException(ermahgerd);
			return [];
		}
	},
});

export const deleteSftpIntegration = CreateProcess({
	method: "DELETE",
	name: "deleteSftpIntegration",
	type: "INTEGRATIONS:DELETE_SFTP",

	request: (props) => {
		const integrationID = get(props, ["params", "integrationID"], props.integrationID);
		return {
			url: `/api/integrations/sftp/${integrationID}`,
		};
	},

	success: (response) => {
		try {
			return response.data;
		} catch (ermahgerd) {
			Sentry.captureException(ermahgerd);
			return [];
		}
	},
});

export const getLog = CreateProcess({
	method: "GET",
	name: "getLog",
	type: "INTEGRATIONS:GET_LOG",

	request: (props) => {
		const queryParams = serializeObject(props.params);
		return {
			url: `/administration/integration_logs/?${queryParams}`,
		};
	},

	success: (response) => {
		try {
			return response.data;
		} catch (ermahgerd) {
			Sentry.captureException(ermahgerd);
			return [];
		}
	},
});

export const checkCsvExists = CreateProcess({
	method: "GET",
	name: "checkCsvExists",
	type: "INTEGRATIONS:CHECK_CSV_EXISTS",

	request: (props) => ({
		url: `/api/sftp/csv-file-check/${props.params.integrationLogID}`,
	}),

	success: (response) => {
		try {
			return response.data;
		} catch (ermahgerd) {
			Sentry.captureException(ermahgerd);
			return [];
		}
	},
});

export const getSignedURL = CreateProcess({
	method: "GET",
	name: "getSignedURL",
	type: "INTEGRATIONS:GET_SIGNED_URL",

	request: (props) => ({
		url: `/api/sftp/signed-url/${props.params.integrationLogID}`,
	}),

	success: (response) => {
		try {
			return response.data;
		} catch (ermahgerd) {
			Sentry.captureException(ermahgerd);
			return [];
		}
	},
});

export const setWalkthroughFinished = CreateProcess({
	method: "POST",
	name: "setWalkthroughFinished",
	type: "INTEGRATIONS:SET_WALKTHROUGH_FINISHED",

	request: () => ({
		url: "/api/integrations/walkthrough/",
	}),

	success: (response) => {
		try {
			return response.data;
		} catch (ermahgerd) {
			Sentry.captureException(ermahgerd);
			return [];
		}
	},
});

export const getWalkthroughFinished = CreateProcess({
	method: "GET",
	name: "getWalkthroughFinished",
	type: "INTEGRATIONS:GET_WALKTHROUGH_FINISHED",

	request: () => ({
		url: "/api/integrations/walkthrough/",
	}),

	success: (response) => {
		try {
			return response.data;
		} catch (ermahgerd) {
			Sentry.captureException(ermahgerd);
			return [];
		}
	},
});

export const getDeactivatedEmployeeNames = CreateProcess({
	method: "GET",
	name: "GetDeactivatedEmployeeNames",
	type: "USERS:GET_DEACTIVATED_EMPLOYEE_NAMES",

	request: (props) => {
		const { params } = props;

		return {
			url: `/administration/integration-logs/${params.integrationLogID}/deactivated-employees/`,
		};
	},

	success: (response) => response.data.results[0],
});

export const getEmployeesRequiringApprovalToDeactivate = CreateProcess({
	method: "GET",
	name: "GetEmployeesRequiringApprovalToDeactivate",
	type: "USERS:GET_EMPLOYEES_REQUIRING_APPROVAL_TO_DEACTIVATE",

	request: (props) => {
		const { params } = props;

		return {
			url: `/administration/integration-logs/${params.integrationLogID}/employees-requiring-approval-to-deactivate/`,
		};
	},

	success: (response) => response.data.results[0],
});
